/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, {lazy, ReactElement, Suspense, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {GlobalStyle} from '../styles/global-styles';
import {NotFoundPage} from './components/NotFoundPage/Loadable';
import {environment} from '../utils/constants';
import Navigation from './components/Navigation';
import {combineSagas} from './combineSagas';
import PdfDocument from './pages/DocumentView/components/PdfDocument';
import i18n from '../i18n';
import {initReactI18next} from 'react-i18next';
import DownloadLimitPage from './components/DownloadLimit';
import DocumentViewWrapper from './pages/DocumentView/DocumentViewWrapper';
import {XmlDocument} from './pages/DocumentView/components/XmlDocument';
import AboutPage from './components/About/AboutPage';

// Lazy load pages
const SearchForm = lazy(() => import('./pages/Search'));
const DataCoverage = lazy(() => import('./pages/DataCoverage'));

export const RedirectPage = ({ url }) => {
    useEffect(() => {
        window.location.replace(url);
    }, [url]);
    return null;
};

export const App = (): ReactElement => {
    // Change language of the translations service dinamically
    let helpUrl: string = 'https://www.epo.org/searching-for-patents/technical/publication-server/help';
    const [language, setLanguage] = useState<string | null>(null);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const langFromUrl = urlParams.get('lg');

        if (langFromUrl) {
            // If lang is present in URL params, use it
            setLanguage(langFromUrl);
            localStorage.setItem('lang', langFromUrl);
            localStorage.setItem('i18nextLng', langFromUrl);
        } else {
            // Otherwise, use the value from localStorage
            const langFromLocalStorage = localStorage.getItem('lang');
            setLanguage(langFromLocalStorage);
        }
    }, []);
    const selectedLanguage = language;

    switch (language) {
        case 'en':
            helpUrl = helpUrl.concat('.html');
            break;
        case 'fr':
            helpUrl = helpUrl.concat('_fr.html');
            break;
        case 'de':
            helpUrl = helpUrl.concat('_de.html');
            break;
        default:
            helpUrl = helpUrl.concat('.html');
            break;
    }

    i18n.use(initReactI18next) // passes i18n down to react-i18next
        .init({ lng: selectedLanguage || 'en' });
    combineSagas();

    let baseUri = '/' + window.location.pathname.split('/')[1] + '/';

    const currentUrl = window.location.href;

    if (currentUrl.startsWith('https://p-pins') || currentUrl.startsWith('https://p-epslod') || currentUrl.startsWith('https://np-pins')) {
        baseUri = baseUri + window.location.pathname.split('/')[2] + '/';
    }
    if (currentUrl.startsWith('http://localhost')) {
        baseUri = `${environment.baseUri}`;
    }

    return (
        <BrowserRouter basename={baseUri}>
            <Helmet defaultTitle="EPO - European Publication Server">
                <meta name="description" content="EPO - European Publication Server" />
            </Helmet>
            <GlobalStyle />

            {/* App Root Routes */}
            <Suspense fallback="Loading...">
                <Routes>
                    <Route
                        path="/"
                        element={
                            <Navigation>
                                <SearchForm />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/data-coverage"
                        element={
                            <Navigation>
                                <DataCoverage />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/document/:documentType/:publicationNumber/:kindCode/:publicationDate"
                        element={
                            <Navigation>
                                <DocumentViewWrapper />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/document/:documentType/:publicationNumber/:kindCode"
                        element={
                            <Navigation>
                                <DocumentViewWrapper />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/pdf-document"
                        element={
                            <Navigation>
                                <DocumentViewWrapper type="pdf" />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/xml-document"
                        element={
                            <Navigation>
                                <DocumentViewWrapper type="xml" />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/result-page"
                        element={
                            <Navigation>
                                <DocumentViewWrapper type="xml" />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/limit-exceeded"
                        element={
                            <Navigation>
                                <DownloadLimitPage />
                            </Navigation>
                        }
                    />
                    <Route
                        path="/about"
                        element={
                            <Navigation>
                                <AboutPage />
                            </Navigation>
                        }
                    />
                    <Route path="/xml/:xmlId" element={<XmlDocument />} />
                    <Route path="/pdf/:pdfId" element={<PdfDocument />} />
                    <Route path="/help" element={<RedirectPage url={helpUrl} />} />
                    <Route
                        path="*"
                        element={
                            <Navigation>
                                <NotFoundPage />
                            </Navigation>
                        }
                    />
                </Routes>
            </Suspense>
        </BrowserRouter>
    );
};
